/* eslint-disable no-console */
import React from "react";
import { useWSClient, sendMessage } from "./useWSClient";
// TODO: FIX FRONT IS DEPENDENT ON API TYPES

import type {
  Channel,
  ServerMessage,
} from "../../../../service-diagnostics-core/src/websocket/Message";
import {
  addOnMessage,
  getPayload,
  setPayload,
  getState,
  removeOnMessage,
  getChannels,
} from "./channelMultiplexerState";

type OnMessage<T extends Channel> = (message: ServerMessage<T>["payload"]) => void;
type Unsubscribe = () => void;
type Subscribe<T extends Channel> = (
  channel: T,
  onMessage: OnMessage<T>,
  once: boolean,
) => Unsubscribe;

// return a function that can be used to add/remove channels with multiplexing on a single websocket connection
// received data is cached in the channelMultiplexerState
export const useChannelMultiplexer = (): Subscribe<Channel> => {
  const handleOnConnect = React.useCallback((client: WebSocket) => {
    const channels = getChannels();
    if (channels.length) {
      console.debug(
        "ws:subscribe:all",
        channels.map(c => c.key),
      );
      sendMessage(client, { type: "subscribe", channels });
    }

    client.onmessage = (event: MessageEvent<string>) => {
      const message = JSON.parse(event.data) as ServerMessage;
      if (message.type === "channel") {
        const { channel, payload } = message;
        const channelState = getState(channel);
        if (channelState === undefined) {
          console.error(`ws:message:${channel.key}`, "ignored, no local state", message.payload);
          return; // channel not subscribed
        }
        console.debug(`ws:message:${channel.key}`, message.payload);
        const { onMessages, once } = channelState;
        setPayload(channel, payload);

        const firstOnMessage = onMessages[0];
        if (!firstOnMessage) throw new Error("no onMessage");
        if (once) {
          firstOnMessage(payload);
        } else {
          for (const onMessage of onMessages) onMessage(message.payload);
        }
      }
    };
  }, []);
  const client = useWSClient(handleOnConnect);

  const clientRef = React.useRef(client);
  clientRef.current = client;

  const add = React.useMemo(
    () =>
      <T extends Channel>(channel: T, onMessage: OnMessage<T>, once: boolean) => {
        if (addOnMessage(channel, onMessage, once)) {
          if (clientRef.current?.readyState === WebSocket.OPEN) {
            console.debug(`ws:subscribe:${channel.key}`, channel);
            sendMessage(clientRef.current, { type: "subscribe", channels: [channel] });
          }
        } else if (!once) {
          const payload = getPayload(channel);
          if (payload !== undefined) onMessage(payload);
        }
      },
    [],
  );

  const remove = React.useMemo(
    () =>
      <T extends Channel>(channel: T, onMessage: OnMessage<T>) => {
        if (removeOnMessage(channel, onMessage)) {
          console.debug(`ws:unsubscribe:${channel.key}`);
          sendMessage(clientRef.current, { type: "unsubscribe", channels: [channel] });
        }
      },
    [],
  );

  return React.useCallback(
    <T extends Channel>(channel: T, onMessage: OnMessage<T>, once: boolean) => {
      add(channel, onMessage, once);
      return () => {
        // setTimeout allows some overlap, avoiding disconnecting and reconnecting the same channel on the same tick
        // when a component is unmounted and immediatly remounted
        window.setTimeout(() => {
          // if WS closed or closing, no need to unsubscribe on backend
          if (clientRef.current?.readyState === WebSocket.OPEN) remove(channel, onMessage);
        });
      };
    },
    [add, remove],
  );
};

export const ChannelMultiplexerContext = React.createContext<Subscribe<Channel> | undefined>(
  undefined,
);

// context that enables useChannel
export const ChannelMuliplexerProvider = ({ children }: { children: React.ReactNode }) => (
  <ChannelMultiplexerContext.Provider value={useChannelMultiplexer()}>
    {children}
  </ChannelMultiplexerContext.Provider>
);

export default ChannelMuliplexerProvider;
